import { useState } from 'react';
import {
  BriefcaseBusiness,
  CalendarCheck2,
  CalendarClock,
  CalendarOff,
  CalendarRange,
  PartyPopper,
} from 'lucide-react';

import { Select } from '@/components/ui/Select';
import { StatCard } from '@/components/ui/StatCard';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MONTHS } from '../constants';
import type { OfficeCalendarSession } from '../types/calendar';
import { PANEL } from './RailPanel';

export interface WorkingDaysSummaryProps {
  data: OfficeCalendarSession | null;
  isLoading: boolean;
  /** The session being read — `''` for the current one. */
  academicYearUuid: string;
  onAcademicYearChange: (uuid: string) => void;
}

const ICON = 'h-5 w-5 text-white';

/**
 * The hints here are the explanation of each figure — "of 365 days in the
 * year", "Plus 2 days on a weekly off" — so they wrap instead of being cut to
 * one line as other dashboards' tiles are.
 */
const FULL_HINT = 'line-clamp-none';

/** "12 days", "1 day" — every figure on this panel is a count of days. */
function days(count: number): string {
  return `${count} ${count === 1 ? 'day' : 'days'}`;
}

/** The month picker's value for the whole session. */
const ALL_MONTHS = '';

/** "October 2026" from `2026-10`. */
function monthLabel(month: string): string {
  return `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}

/**
 * The session in days, or one month of it: how many the school teaches, how
 * many it is closed and why, and how far through it is.
 *
 * Every figure is the server's (`dayCount`), worked out from the session's
 * dates, the weekly offs and the declared holidays — nothing is counted here,
 * so this panel and the attendance report cannot disagree. A holiday that falls
 * on a weekly off is counted once, as the weekly off; the holiday card says how
 * many were set aside that way so the totals still add up for the reader.
 */
export function WorkingDaysSummary({
  data,
  isLoading,
  academicYearUuid,
  onAcademicYearChange,
}: WorkingDaysSummaryProps) {
  const [month, setMonth] = useState(ALL_MONTHS);
  const session = data?.dayCount;
  const isLoadingFigures = isLoading || !session;

  // A month from the session before is not in this one, so changing the year
  // falls back to the whole of it rather than to cards with nothing behind them.
  const selectedMonth = session?.months.find((option) => option.month === month) ?? null;
  const count = selectedMonth ?? session;
  // Named, not "this month": the month picked is as often a past or future one.
  const span = selectedMonth ? monthLabel(selectedMonth.month) : 'the year';

  const monthOptions = [
    { value: ALL_MONTHS, label: 'All months' },
    ...(session?.months ?? []).map((option) => ({
      value: option.month,
      label: monthLabel(option.month),
    })),
  ];

  // The current session is the empty value, so the address of "this year"
  // never goes stale when the school rolls over to the next one.
  const yearOptions = (data?.academicYears ?? []).map((year) => ({
    value: year.isCurrent ? '' : year.uuid,
    label: year.isCurrent ? `${year.name} (current)` : year.name,
  }));

  // Nothing to draw once the load has failed: the calendar below says why.
  if (!isLoading && !session) return null;

  return (
    <section
      className={cn(PANEL, 'flex flex-col gap-4 p-3.5 sm:p-5 animate-slide-down delay-50')}
      aria-labelledby="working-days-heading"
    >
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 id="working-days-heading" className="text-base font-black tracking-tight text-ink">
            Working days
          </h2>
          <p className="mt-0.5 text-sm text-ink-3">
            Counted from the academic year&apos;s dates, your weekly offs and declared holidays.
          </p>
        </div>
        <div className="grid w-full grid-cols-1 gap-3 sm:w-auto sm:grid-cols-2">
          <div className="sm:w-48">
            <Select
              label="Month"
              options={monthOptions}
              value={selectedMonth ? month : ALL_MONTHS}
              disabled={monthOptions.length === 1}
              onChange={(event) => setMonth(event.target.value)}
            />
          </div>
          <div className="sm:w-56">
            <Select
              label="Academic year"
              options={yearOptions}
              value={academicYearUuid}
              disabled={yearOptions.length === 0}
              onChange={(event) => {
                setMonth(ALL_MONTHS);
                onAcademicYearChange(event.target.value);
              }}
            />
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 xl:grid-cols-6">
        <StatCard
          label="Total working days"
          value={count?.workingDays ?? 0}
          hint={count ? `of ${days(count.totalDays)} in ${span}` : undefined}
          variant="emerald"
          isLoading={isLoadingFigures}
          hintClassName={FULL_HINT}
          icon={<BriefcaseBusiness className={ICON} />}
          layout="stacked"
        />
        <StatCard
          label="Non-working days"
          value={count?.nonWorkingDays ?? 0}
          hint={
            count
              ? `${days(count.weeklyOffDays)} weekly off · ${days(count.holidayDays)} holiday`
              : undefined
          }
          variant="rose"
          isLoading={isLoadingFigures}
          hintClassName={FULL_HINT}
          icon={<CalendarOff className={ICON} />}
          layout="stacked"
        />
        <StatCard
          label="Working days completed"
          value={count?.workingDaysCompleted ?? 0}
          hint="Up to and including today"
          variant="indigo"
          isLoading={isLoadingFigures}
          hintClassName={FULL_HINT}
          icon={<CalendarCheck2 className={ICON} />}
          layout="stacked"
        />
        <StatCard
          label="Remaining working days"
          value={count?.workingDaysRemaining ?? 0}
          hint={selectedMonth ? `Still to come in ${span}` : 'Still to come this year'}
          variant="cyan"
          isLoading={isLoadingFigures}
          hintClassName={FULL_HINT}
          icon={<CalendarClock className={ICON} />}
          layout="stacked"
        />
        <StatCard
          label="Total holidays"
          value={count?.holidayDays ?? 0}
          hint={
            count && count.holidaysOnWeeklyOffs > 0
              ? `Plus ${days(count.holidaysOnWeeklyOffs)} on a weekly off, not counted twice`
              : 'Declared days the school is closed'
          }
          variant="amber"
          isLoading={isLoadingFigures}
          hintClassName={FULL_HINT}
          icon={<PartyPopper className={ICON} />}
          layout="stacked"
        />
        <StatCard
          label="Academic year"
          value={data ? data.academicYear.name : '—'}
          hint={data ? `${formatDate(data.startDate)} – ${formatDate(data.endDate)}` : undefined}
          variant="slate"
          isLoading={isLoadingFigures}
          hintClassName={FULL_HINT}
          icon={<CalendarRange className={ICON} />}
          layout="stacked"
        />
      </div>
    </section>
  );
}
